import QUERT_VIKA_SESSION from './graphql/queryVikaSession';
import COPY_VIKA_SHARE from './graphql/copyVikaShare';
import CLOUD_DESKTOP_ID from './graphql/cloudDesktopId';
import DESKTOP_INSTANCE_DOWNLOAD_FILE from './graphql/desktopInstanceDownloadFile';

import { getDomain } from '@/utils/common';
import { ApolloError } from '@forchange/cleman-client';
import { isProd } from '@forchange/utils';
import { ISandboxConfig } from '../SandboxIframe/types';
import { IChatGPTConfig } from '../ChatGPTIframe/util';
import { IJupyterConfig } from '@/components/resources/dialog/JupyterIframe/types';
import {
  JupyterSandboxCourseRetrievePractice,
  JupyterSandboxPracticeCopyFiles,
  JupyterSandboxPracticeLogin,
  JupyterSandboxPracticeTerminate,
  JupyterSandboxUserRetrievePractice
} from '@/components/resources/dialog/PracticeCourt/graphql/jupyterSandbox';
import { IAIPaintConfig } from '../AIPaintIframe/util';

export type PracticeCourtOptions = {
  vika?: {
    shareId: string;
  };
  desktop?: {
    desktopId: string;
  };
  sandbox?: ISandboxConfig;
  chatgpt?: IChatGPTConfig;
  jupyter?: IJupyterConfig;
  url?: {
    url: string;
  };
  aipaint?: IAIPaintConfig
};

export type PracticeData = {
  url: string;
  userId?: string;
  instanceId?: string;
  practiceId?: string;
};

async function vika(
  this: any,
  type: string,
  options: {
    shareId: string;
  },
  currentUserId?: string
): Promise<PracticeData | undefined> {
  const { url, userId } = await initVikaSession.call(this, currentUserId);
  const nodeId = await copyVikaShare.call(this, options.shareId, userId);
  if (!url || !nodeId) return;
  return {
    url: url + nodeId,
    userId
  };
}

async function desktop(
  this: any,
  options: { desktopId: string },
  courseId?: string
): Promise<PracticeData | undefined> {
  const data = await initDesktop.call(this, options, courseId);
  if (data?.url && data.instanceId) {
    return data;
  }
}

export async function desktopFileUpload(
  this: any,
  params: {
    fileName: string;
    src: string;
    desktopId: string;
    courseId: string;
  }
) {
  const { desktopId, courseId, fileName, src } = params;
  try {
    const res = await window.$getClemanClient().apollo.mutate({
      mutation: CLOUD_DESKTOP_ID,
      variables: {
        desktopId,
        courseId
      }
    });
    const status = res.data.retrieveDesktopInstance.status;
    if (status === 'RUNNING' || status === 'USING') {
      const instanceId = res.data.retrieveDesktopInstance.id;
      const uploadData = await window.$getClemanClient().apollo.mutate({
        mutation: DESKTOP_INSTANCE_DOWNLOAD_FILE,
        variables: {
          fileName,
          instanceId,
          src
        }
      });
      this.$Message.success({
        time: 1500,
        content: `附件 ${fileName} 已上传到桌面`
      });
    } else {
      this.$Message.warn({
        time: 1500,
        content: '云桌面尚未就绪，请稍后再试'
      });
    }
  } catch (e) {
    if (e instanceof ApolloError) {
      this.$Message.error({
        time: 1500,
        content: e.graphQLErrors[0].message
      });
    }
  }
}

export async function desktopFileUploadAuto(
  this: any,
  params: {
    fileName: string;
    src: string;
    desktopId: string;
    courseId: string;
    pollingInterval?: number;
  }
) {
  const { desktopId, courseId, fileName, src, pollingInterval } = params;
  let timer = 0;
  // 轮询，直到状态为 running 或 using
  timer = window.setInterval(async () => {
    try {
      const res = await window.$getClemanClient().apollo.mutate({
        mutation: CLOUD_DESKTOP_ID,
        variables: {
          desktopId,
          courseId
        }
      });
      const status = res.data.retrieveDesktopInstance.status;
      if (status === 'RUNNING' || status === 'USING') {
        window.clearInterval(timer);
        const instanceId = res.data.retrieveDesktopInstance.id;
        const uploadData = await window.$getClemanClient().apollo.mutate({
          mutation: DESKTOP_INSTANCE_DOWNLOAD_FILE,
          variables: {
            fileName,
            instanceId,
            src
          }
        });
        this.$Message.success({
          time: 1500,
          content: `附件 ${fileName} 已上传到桌面`
        });
      }
    } catch {
      window.clearInterval(timer);
    }
  }, 1000 * (pollingInterval || 6));
}

export async function getDesktopInstanceId(desktopId: string, courseId = '') {
  const res = await window.$getClemanClient().apollo.mutate({
    mutation: CLOUD_DESKTOP_ID,
    variables: {
      desktopId,
      courseId
    }
  });
  return res;
}

async function initDesktop(this: any, options: { desktopId: string }, courseId = '') {
  let url = '';
  try {
    const res = await getDesktopInstanceId(options.desktopId, courseId);
    const id = res.data.retrieveDesktopInstance.id;
    if (!id) {
      return;
    }
    if (isProd()) {
      url = `/client/desktop/?instanceId=${id}`;
    } else {
      url = `https://test.cleword.cn/client/desktop/?instanceId=${id}`;
    }
    return { url, instanceId: id };
  } catch (e) {
    if (e instanceof ApolloError) {
      this.$Message.error({
        time: 5000,
        content: e.graphQLErrors[0].message
      });
    }
  }
}

export async function initVikaSession(this: any, currentUserId?: string) {
  const noteId = await this.$getClemanClient().apollo.mutate({
    mutation: QUERT_VIKA_SESSION,
    variables: {
      userId: currentUserId
    }
  });
  let url;
  let vikaUser;
  try {
    vikaUser = noteId.data.currentUser.sandbox.vikaUser;
    const session = vikaUser.session;
    const domain = getDomain(window.location.hostname);
    document.cookie = `${session.name}=${session.value};expires=${session.expires};path=/;domain=${domain}`;
    url = vikaUser.url + '/workbench/';
  } catch (error) {
    console.log(error);
  }
  return {
    url,
    userId: vikaUser.userId
  };
}

async function copyVikaShare(this: any, shareId: string, userId: string) {
  let nodeId;
  try {
    const res = await this.$getClemanClient()
      .apollo.mutate({
        mutation: COPY_VIKA_SHARE,
        variables: {
          shareId,
          userId
        }
      })
      .catch((e: Error) => {
        if (e instanceof ApolloError) {
          this.$Message.error({
            content: e.graphQLErrors[0].message,
            time: 5000
          });
        }
      });
    nodeId = res.data.sandboxVikaCopyDatasheetToMySpace.nodeId;
  } catch (error) {
    console.log(error);
  }
  return nodeId;
}

export async function retrieveJupyterSandboxPracticeId(
  courseId: string | undefined
): Promise<string> {
  const res = await window.$getClemanClient().apollo.mutate({
    mutation: courseId ? JupyterSandboxCourseRetrievePractice : JupyterSandboxUserRetrievePractice,
    variables: {
      ...(courseId ? { courseId } : null)
    }
  });

  return courseId
    ? res.data.jupyterSandboxCourseRetrievePractice.id
    : res.data.jupyterSandboxUserRetrievePractice.id;
}

export async function jupyterSandboxPracticeCopyFiles(
  id: string,
  sourceUser: string,
  files: string[]
) {
  await window.$getClemanClient().apollo.mutate({
    mutation: JupyterSandboxPracticeCopyFiles,
    variables: {
      id,
      fromId: sourceUser,
      files
    }
  });
}

export async function jupyterSandboxPracticeLogin(id: string, path: string): Promise<string> {
  const res = await window.$getClemanClient().apollo.query({
    query: JupyterSandboxPracticeLogin,
    variables: {
      id,
      path
    }
  });

  return res.data.jupyterSandboxPractice.loginUrl;
}

export async function jupyterSandboxPracticeTerminate(id: string) {
  await window.$getClemanClient().apollo.mutate({
    mutation: JupyterSandboxPracticeTerminate,
    variables: {
      id
    }
  });
}

export async function initJupyter(
  this: any,
  options: IJupyterConfig | undefined,
  courseId: string | undefined
) {
  try {
    const id = await retrieveJupyterSandboxPracticeId(courseId);

    if (options && options.mainFile && options.sourceUser) {
      await jupyterSandboxPracticeCopyFiles(id, options.sourceUser, [
        options.mainFile,
        ...(options.otherFiles ? options.otherFiles : [])
      ]);
    }

    const url = await jupyterSandboxPracticeLogin(
      id,
      options && options.mainFile ? options.mainFile : ''
    );
    return {
      url,
      practiceId: id
    };
  } catch (e) {
    let message;
    if (e instanceof ApolloError) {
      message = e.graphQLErrors[0].message;
    } else {
      message = e.message;
    }

    this.$Message.error({
      time: 5000,
      content: message
    });

    throw new Error(message);
  }
}

export async function terminateJupyter(this: any, id: string) {
  try {
    await jupyterSandboxPracticeTerminate(id);
  } catch (err) {}
}

async function jupyter(
  this: any,
  options: IJupyterConfig | undefined,
  courseId: string | undefined
): Promise<PracticeData | undefined> {
  return await initJupyter.call(this, options, courseId);
}

export default async function initSpecial(
  this: any,
  params: {
    type: string;
    options: PracticeCourtOptions;
    userId?: string;
    courseId?: string;
  }
): Promise<PracticeData | undefined> {
  let url: PracticeData | undefined;
  const { type, options, userId, courseId } = params;
  switch (type) {
    case 'vika':
      if (!options.vika) {
        this.$Message.error({
          time: 5000,
          content: '缺少必须参数'
        });
      }
      url = await vika.call(this, type, options.vika!, userId);
      break;
    case 'desktop':
      if (!options.desktop) {
        this.$Message.error({
          time: 5000,
          content: '缺少必须参数'
        });
      }
      url = await desktop.call(this, options.desktop!, courseId);
      break;
    case 'jupyter':
      url = await jupyter.call(this, options.jupyter, courseId);
  }
  return url;
}
